import { Button } from '@casumo/dojo-react';
import { Close } from '@casumo/dojo-react-icons';
import { memo, useCallback, useEffect, useState } from 'react';
import useFetchErrorContent from '../../api/hooks/useFetchErrorContent';
import useRegister from '../../api/hooks/useRegister';
import { WarningIcon } from '../../assets/icons';
import Drawer from '../../design-system/drawer';
import Skeleton from '../../design-system/skeleton';
import useAreRequiredFieldsFilled from '../../hooks/useAreRequiredFieldsFilled';
import useFieldValidator from '../../hooks/useFieldValidator';
import { useFlowContext } from '../../hooks/useFlowContext';
import useFlowNavigation from '../../hooks/useFlowNavigation';
import useMarketInfo from '../../hooks/useMarketInfo';
import { isRegistrationInFlight, useRegistrationStore } from '../../store/store';
import { TEST_ID } from '../../utils/constants';
import { GtmEvents } from '../../utils/gtm/gtm';
import identityPasswordValidation from '../../utils/identityPasswordValidation';
import { checkIdentificationNumberES } from '../../utils/utils';

type TReviewDetailsButton = {
  handleNext: ({
    gtmPayload,
    redirectTo,
  }: {
    gtmPayload?: Record<string, string>;
    redirectTo?: string;
    canProceed?: boolean;
  }) => void;
  label?: string;
  gtmPayload?: Record<string, string>;
  name?: string;
};

const ReviewModalsSkeleton = () => (
  <div className="flex flex-col w-full h-full items-start justify-start p-sm tablet:px-lg gap-lg">
    <div className="flex w-full gap-3xs items-center justify-between">
      <WarningIcon />
      <Close
        data-testid="close-button"
        className="cursor-pointer text-white"
        svgProps={{
          height: '24',
          width:  '24',
        }}
      />
    </div>
    <Skeleton height="h-[50px] w-full" />
    <Skeleton height="h-[60px] w-full" />
    <div className="flex justify-center items-end w-full grow p-sm tablet:px-lg ">
      <Skeleton height="h-[50px] w-[90%]" />
    </div>
  </div>
);

const ReviewDetailsButton = ({
  label,
  name,
  handleNext,
}: TReviewDetailsButton) => {
  const { formData, registerError, setRegisterError } = useRegistrationStore();
  const { registerUser, isRegistering } = useRegister();
  const { config } = useFlowContext();
  const marketInfo = useMarketInfo();
  const { validateAllFields } = useFieldValidator(config);
  const {
    step,
  } = useFlowNavigation(marketInfo, config);

  const currentStepConfig = config.steps[step]

  const isNextEnabled = useAreRequiredFieldsFilled(formData, currentStepConfig);

  const [drawerType, setDrawerType] = useState<
    | 'identity_password'
    | 'wrong_personal_number'
    | 'incorrect_details'
    | string
    | undefined
    | null
  >();

  useEffect(() => {
    setDrawerType(registerError);
  }, [registerError]);

  const setDrawerIsOpen = useCallback((isOpen: boolean) => {
    if (isOpen) {
      setDrawerType('identity_password');
    } else {
      setDrawerType(undefined);
    }
  }, []);

  const { data: modalData, loading: errorDataLoading } = useFetchErrorContent(drawerType, { skip: !drawerType });

  const onReviewButton = useCallback(async () => {

    if (!formData || isRegistrationInFlight()) return;

    const IdentificationNumberType = formData?.['NIE'] ? 'NIE' : 'DNI';
    const isValid = checkIdentificationNumberES(
      formData?.[IdentificationNumberType],
      IdentificationNumberType,
    );

    if (!isValid) {
      GtmEvents.wrongFormatIdentificationNumberEs();
      setDrawerType('wrong_personal_number');

      return;
    }

    if (!identityPasswordValidation(formData?.plainTextPassword, formData)) {
      setDrawerType('identity_password');

      return;
    }

    if (!isNextEnabled) {
      validateAllFields()

      return
    }
    await registerUser(formData);

  }, [formData, registerUser, isNextEnabled, validateAllFields]);

  const onCloseDrawer = useCallback(() => {
    setRegisterError(null);
    setDrawerType(undefined);
  }, [setDrawerType, setRegisterError]);

  const onClickPrimary = useCallback(() => {
    if (drawerType === 'identity_password') {
      setDrawerType(undefined);
      handleNext({ gtmPayload: {}, redirectTo: '-4', canProceed: true });
    }
    if (drawerType === 'wrong_personal_number') {
      setDrawerType(undefined);
    }
    if (drawerType === 'incorrect_details') {
      setDrawerType(undefined);
      setRegisterError(null);
    }
  }, [drawerType, handleNext, setRegisterError]);

  if (!name) return null;

  return (
    <>
      <Button
        width="full"
        data-testid={TEST_ID.BUTTON_PRIMARY}
        onClick={onReviewButton}
        disabled={isRegistering}
      >
        {label}
      </Button>
      <Drawer isOpen={Boolean(drawerType)} setIsOpen={setDrawerIsOpen}>
        <>
          {errorDataLoading ? (
            <ReviewModalsSkeleton />
          ) : (
            <div className="flex flex-col relative grow w-full h-full">
              <div className="flex flex-col grow overflow-y-auto">
                <div className="flex gap-3xs items-center justify-between text-white p-md tablet:px-lg">
                  <WarningIcon />
                  <Close
                    data-testid="close-button"
                    className="cursor-pointer"
                    svgProps={{
                      height: '24',
                      width:  '24',
                    }}
                    onClick={onCloseDrawer}
                  />
                </div>
                <p className="text-heading-lg text-white p-sm pt-xs w-full tablet:px-lg">
                  {modalData?.title}
                </p>
                <div className="flex flex-col gap-2xs p-sm w-full tablet:px-lg grow">
                  <p className="text-white">{modalData?.message}</p>
                </div>
              </div>
              <div
                className={`flex flex-col p-md gap-xs
              bg-gradient-to-t from-purple-90 via-70% via-purple-90 to-purple-90/0`}
              >
                <Button
                  width="full"
                  variant="primary-dark"
                  className="bg-yellow-20"
                  data-testid={TEST_ID.BUTTON_PRIMARY}
                  onClick={onClickPrimary}
                >
                  {modalData?.btnMainText}
                </Button>
              </div>
            </div>
          )}
        </>
      </Drawer>
    </>
  );
};

const Memoized = memo(ReviewDetailsButton);
Memoized.displayName = 'ReviewDetailsButton';

export default Memoized;
